<template>
    <div class="my-8">

        <h3 class="text-lg text-center font-medium leading-6 text-gray-900 sm:text-2xl">
            Almost there. We have a few more questions to help speed up quoting!
        </h3>

        <ListBoxSelectWithImage labelMain="What is your preferred leadtime:" :options="leadTime" requires="True"
            v-model="form.leadTime" />
        <br />
        <Alert message="*Assuming in-stock materials and finishes are used" />

        <label for="comment" class="block text-sm font-medium text-gray-700 my-3"> What is your timeline, or ideal
            in-hand date?</label>

        <vue-tailwind-datepicker as-single use-range overlay v-model="form.dateValue" separator=" to "
            :formatter="formatter" :disabled-dates="disabledDates" />

        <div v-if="dateWarning" class="text-red-500 text-xs">
            Selected date is sooner than 3 weeks from today!
        </div>

        <BaseRadioButtonGroup
            label="If you have multiple parts, would you like those parts to be considered as an assembly?"
            v-model="form.mutiplePartsAssembly" group="`multipleParts`" :options="yesNo" />

        <BaseRadioButtonGroup
            label="Can we quote with material substitutions in order to achieve the fastest manufacturing turnaround possible?"
            v-model="form.quoteSubstitutions" group="quoteSubstitutions" :options="yesNo" />

        <BaseRadioButtonGroup
            label="Can we make minor design modifications in order to match our manufacturing capabilities? (All changes will be outlined to you and approved before production)"
            v-model="form.minorDesignModifications" group="`minorDesignModifications`" :options="yesNo" />

        <BaseRadioButtonGroup label="Are there any critical aspects of your design that we should note?"
            v-model="form.criticalAspects" group="`criticalAspects`" :options="yesNo" />

        <BaseTextArea v-if="form.criticalAspects == 'Yes'"
            label="Please tell us about the critical aspects of your design." rows="2"
            placeholder="Enter your information here" v-model="form.descriptionCriticalAspects" />

        <ListBoxSelectWithImage labelMain="Does your design need to be compliant to the following programs?"
            :options="compliantPrograms" v-model="form.compliantPrograms" />

        <div class="my-4">
            <label for="comment" class="block text-sm font-medium text-gray-700"> Do you require any of the following
                documentation? </label>

            <div class="my-3">
                <input type="checkbox" value="REACH" id="REACH"
                    class="h-4 w-4 mx-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    v-model="form.documentation" />
                <label for="REACH">REACH</label>
                <input type="checkbox" value="RoHS" id="RoHS" v-model="form.documentation"
                    class="h-4 w-4 mx-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <label for="RoHS">RoHS</label>
                <input type="checkbox" value="Traceability" id="Traceability" v-model="form.documentation"
                    class="h-4 w-4 mx-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <label for="Traceability">Traceability</label>
            </div>
        </div>

        <BaseRadioButtonGroup label="Does your design require Certificate of Conformance or Inspection Services?"
            group="`inspection`" :options="yesNo" v-model="form.inspectionCoC" />

        <Alert
            message="*Pricing and leadtime may be affected, depending on the level of certification or inspection that is required." />

        <ListBoxSelectWithImage v-if="form.inspectionCoC == 'Yes'" labelMain="Please select your inspection type:"
            :options="certificateOfConformance" v-model="form.typeOfCertificateOfConformance" />

        <BaseRadioButtonGroup
            v-if="(form.typeOfCertificateOfConformance == 'Basic Station') || (form.typeOfCertificateOfConformance == 'Basic Station Custom') || (form.typeOfCertificateOfConformance == 'Premium AS9102') || (form.typeOfCertificateOfConformance == 'Premium AS9102 Custom')"
            label="What frequency of inspection do you require?" v-model="form.frequencyOfInspection"
            :options="frequencyOfInspection" />

        <BaseTextArea
            v-if="(form.typeOfCertificateOfConformance == 'Custom - Certificate of Conformance(C of C)') || (form.typeOfCertificateOfConformance == 'Basic Station Custom') || (form.typeOfCertificateOfConformance == 'Premium AS9102 ') || (form.typeOfCertificateOfConformance == 'Premium AS9102 Custom')"
            label="Please describe the custom inspection you require:" rows="2"
            placeholder="what are you specifically wanting us to inspect or dimension?"
            v-model="form.descriptionCustomInspection" />

        <BaseTextArea
            label="If you are able, please tell us more about your project, its application and your requirements. This information will help our Engineering & Design Services team make the most informed decisions based on your specific needs."
            placeholder="Enter your information here" v-model="form.descriptionAboutProject" />

    </div>

    <!-- modal to show missing fields -->
    <!-- Show modal popup when fields are missing -->
    <TransitionRoot as="template" :show="open">
        <Dialog as="div" class="relative z-10" @close="open = false">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                            <div>
                                <button @click="open = false" ref="cancelButtonRef" v-if="success == true"
                                    class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                    <CheckIcon class="h-6 w-6 text-green-600" aria-hidden="true" />

                                </button>
                                <button @click="open = false" ref="cancelButtonRef" v-if="success == false"
                                    class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                    <XMarkIcon class="h-6 w-6 text-red-600" aria-hidden="true" />

                                </button>

                                <div class="mt-3 text-center sm:mt-5">
                                    <DialogTitle as="p">
                                        <!-- add missing fields -->
                                        <template v-if="success == false">
                                            <p class="font-semibold">
                                                Please fill out the following fields:

                                            </p>
                                            <p v-for="field in missingFields" :key="field"
                                                class="text-xs items-center text-left leading-6 text-red-900">
                                                * {{ field }}
                                            </p>
                                        </template>
                                        <!-- add success message -->
                                        <template v-if="success == true">
                                            <p>
                                                Thank you for your submission. We will be in touch shortly.
                                            </p>
                                        </template>
                                    </DialogTitle>

                                </div>
                            </div>

                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>

    <section class="my-6 flex justify-between">
        <Button v-if="form.quoteType == 'New Design'" label="Back" class="bg-blue-600 text-white p-2"
            @click.prevent="form.step = 'materialFinishes'" />
        <Button v-if="form.quoteType == 'Reorder'" label="Back" class="bg-blue-600 text-white p-2"
            @click.prevent="form.step = 'start'" />

        <Button label="Next" class="bg-blue-600 text-white p-2" @click.prevent="checkMissingField1" />

    </section>
</template>


<script setup>
import { inject, ref, onMounted, computed, watch } from "vue";
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue';

import BaseRadioButtonGroup from "./formComponents/BaseRadioButtonGroup.vue";
import BaseTextArea from "./formComponents/BaseTextArea.vue";
import Button from "./formComponents/Button.vue";
import ListBoxSelectWithImage from "./formComponents/ListBoxSelectWithImage.vue";
import VueTailwindDatepicker from 'vue-tailwind-datepicker';
import { CheckIcon, XMarkIcon } from '@heroicons/vue/20/solid';

import {
    yesNo, leadTime, certificateOfConformance, compliantPrograms, frequencyOfInspection
} from "../constants/options";
import Alert from "./formComponents/Alert.vue";

const form = inject('form');

const formatter = ref({
    date: 'DD MMM YYYY',
    month: 'MMM'
})

// on mounted scroll to top
onMounted(() => {
    window.scrollTo(0, 0);
})

// check fields if they are missing or not 
// check missing fields using watch property of vue js 3 
const missingFields = ref([]);
const success = ref(false);
const open = ref(false);
const dateWarning = ref(false);

// check missing fields on clicking next button
const checkMissingField1 = () => {
    missingFields.value = [];


    // check if the project is compliant with any program
    if (!form.leadTime) {
        missingFields.value.push('What is your preferred leadtime: ');
    }


    if (dateWarning.value) {
        missingFields.value.push('Selected date is sooner than 3 weeks from today');
    }


    if (missingFields.value.length == 0) {
        // success.value = true;
        // open.value = true;
        // move to a next step
        form.step = 'contactInfo';
    } else {
        success.value = false;
        open.value = true;
    }
}

// Disable all past dates including today
const disabledDates = computed(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set time to midnight to disable the entire day
    return {
        from: null,
        to: today, // Disable all dates up to today
    };
});


// Watch for date changes to trigger warnings
watch(() => form.dateValue, (newValue) => {
    if (Array.isArray(newValue)) {
        const minDate = new Date();
        minDate.setDate(minDate.getDate() + 21); // 3 weeks from today

        const startDate = new Date(newValue[0]); // First date in the array
        console.log(form.leadTime); 
        if (form.leadTime === 'Economy Extended 3-Week Service'  && startDate < minDate) {
            console.log('dateWarning');
            dateWarning.value = true;
        } else {
            dateWarning.value = false;
        }
    } else {
        console.warn('Unexpected format for dateValue:', newValue);
    }
});


</script>